import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "../../lib/api";
import { Section, SectionHeader } from "./_helpers";
import { Shield, EyeOff, Download, Plus } from "lucide-react";
import { toast } from "sonner";

export default function BlokkeringPage() {
  const { t } = useTranslation();
  const [blocked, setBlocked] = useState([]);
  const [mutes, setMutes] = useState([]);
  const [bulkInput, setBulkInput] = useState("");
  const [muteInput, setMuteInput] = useState("");
  const [busy, setBusy] = useState(false);

  const loadBlocks = () => api.get("/users/me/blocks").then(r => setBlocked(r.data)).catch(() => {});
  const loadMutes = () => api.get("/users/me/mutes").then(r => setMutes(r.data)).catch(() => {});

  useEffect(() => { loadBlocks(); loadMutes(); }, []);

  const unblock = async (username) => {
    await api.post(`/users/${username}/block`).catch(() => {});
    setBlocked((b) => b.filter((u) => u.username !== username));
  };

  const bulkBlock = async () => {
    const list = bulkInput.split(/[,\s]+/).map(u => u.trim()).filter(Boolean);
    if (list.length === 0) return toast.error(t("blokkering.bulk_empty"));
    setBusy(true);
    try {
      const r = await api.post("/users/me/blocks/bulk", { usernames: list });
      toast.success(t("blokkering.bulk_success", { count: r.data.blocked_count }));
      setBulkInput("");
      loadBlocks();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const muteUser = async () => {
    const u = muteInput.trim();
    if (!u) return;
    try {
      await api.post("/users/me/mute", { username: u });
      toast.success(t("blokkering.mute_success", { user: u }));
      setMuteInput("");
      loadMutes();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const unmute = async (username) => {
    await api.delete(`/users/me/mute/${username}`).catch(() => {});
    setMutes((m) => m.filter((u) => u.muted_username !== username));
  };

  const exportBlocks = async () => {
    try {
      const r = await api.get("/users/me/blocks/export");
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `blokk-liste-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(t("blokkering.exported_toast"));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <>
      <Section testId="blocked-users-section">
        <SectionHeader icon={Shield} title={t("blokkering.blocked_title")}
          desc={t("blokkering.blocked_desc")} />
        {blocked.length === 0 ? (
          <p className="font-fraunces italic text-[var(--cream-soft)]">{t("blokkering.no_blocked")}</p>
        ) : (
          <ul className="divide-y divide-white/5 border-y border-white/10" data-testid="blocked-list">
            {blocked.map((u) => (
              <li key={u.id} className="flex items-center justify-between py-3">
                <span className="font-fraunces italic text-lg text-[var(--cream)]">@{u.username}</span>
                <button onClick={() => unblock(u.username)}
                  className="font-manrope text-xs uppercase tracking-[0.2em] text-[var(--cream-soft)] hover:text-[var(--magenta)]"
                  data-testid={`unblock-${u.username}`}>
                  {t("blokkering.unblock")}
                </button>
              </li>
            ))}
          </ul>
        )}
        {blocked.length > 0 && (
          <div className="mt-4">
            <button onClick={exportBlocks}
              className="btn-ghost inline-flex items-center gap-1.5 text-xs"
              data-testid="export-blocks-btn">
              <Download className="h-3.5 w-3.5" /> {t("blokkering.export_json")}
            </button>
          </div>
        )}
      </Section>

      <Section testId="bulk-block-section">
        <SectionHeader icon={Plus} title={t("blokkering.bulk_title")}
          desc={t("blokkering.bulk_desc")} />
        <div className="space-y-3">
          <textarea
            value={bulkInput}
            onChange={(e) => setBulkInput(e.target.value)}
            placeholder={t("blokkering.bulk_placeholder")}
            rows={3}
            className="input-editorial w-full resize-none"
            data-testid="bulk-block-input"
          />
          <button onClick={bulkBlock} disabled={busy}
            className="btn-magenta inline-flex items-center gap-1.5 text-xs"
            data-testid="bulk-block-submit">
            <Shield className="h-3.5 w-3.5" /> {busy ? t("blokkering.bulk_busy") : t("blokkering.bulk_submit")}
          </button>
        </div>
      </Section>

      <Section testId="mute-section">
        <SectionHeader icon={EyeOff} title={t("blokkering.mute_title")}
          desc={t("blokkering.mute_desc")} />
        <div className="flex gap-2 mb-4">
          <input
            value={muteInput}
            onChange={(e) => setMuteInput(e.target.value)}
            placeholder={t("blokkering.mute_placeholder")}
            className="input-editorial flex-1"
            onKeyDown={(e) => e.key === "Enter" && muteUser()}
            data-testid="mute-input"
          />
          <button onClick={muteUser}
            className="btn-magenta px-4 text-xs inline-flex items-center gap-1.5"
            data-testid="mute-add-btn">
            <EyeOff className="h-3.5 w-3.5" /> {t("blokkering.mute_btn")}
          </button>
        </div>
        {mutes.length === 0 ? (
          <p className="font-fraunces italic text-[var(--cream-soft)]">{t("blokkering.no_muted")}</p>
        ) : (
          <ul className="divide-y divide-white/5 border-y border-white/10" data-testid="muted-list">
            {mutes.map((m) => (
              <li key={m.muted_id} className="flex items-center justify-between py-3">
                <span className="font-fraunces italic text-lg text-[var(--cream)]">
                  <EyeOff className="h-3 w-3 inline mr-1.5 text-[var(--cream-faint)]" />
                  @{m.muted_username}
                </span>
                <button onClick={() => unmute(m.muted_username)}
                  className="font-manrope text-xs uppercase tracking-[0.2em] text-[var(--cream-soft)] hover:text-[var(--magenta)]"
                  data-testid={`unmute-${m.muted_username}`}>
                  {t("blokkering.unblock")}
                </button>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </>
  );
}
